import {FC, useEffect, useMemo, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Col, Row, Spinner} from 'react-bootstrap'
import DataTable from '../../../../ui-kit/DataTable'
import {Role} from './core/_models'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import {useIntl} from 'react-intl'
import {deleteRole, getAllRoles} from './core/_requests'
import {Link} from 'react-router-dom'
import {showSwal} from '../../../../ui-kit'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'



const ManageAvailableRolesPage: FC<{subject: string}> = ({subject}) => {
  const axiosPrivate = useAxiosPrivate()
  const [internalLoading, setInternalLoading] = useState(false)
  const ability = useAbility(AbilityContext)
  const [roles, setRoles] = useState<Role[]>([])
  const intl = useIntl()
  const columnHelper = createColumnHelper<Role>()
  const columns: ColumnDef<Role, any>[] = useMemo(
    () => [
      columnHelper.accessor('role', {
        header: 'Role',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('economy', {
        header: 'Economy',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('id', {
        header: 'Actions',
        cell: (info) => {
          return (
            <div className='d-flex gap-3 justify-content-center'>
              <Link to={`/manage-roles/${info.getValue()}`}>
                <span className='fa fa-eye text-primary'></span>
              </Link>

              {ability.can('delete', subject) ? (
                <Link
                  to='#'
                  className='link-danger'
                  onClick={() => handleRoleDeletion(info.getValue())}
                >
                  <span className='fa fa-trash'></span>
                </Link>
              ) : (
                ''
              )}
            </div>
          )
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    []
  )

  const handleRoleDeletion = (id) => {
    showSwal({
      title: 'Are you sure you want to delete this role?',
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      deleteRole(axiosPrivate, id)
        .then((res) => {
          if (res.status === 200) {
            showSwal('Role deleted', undefined, 'success').then(() => {
              setRoles((prev) => prev?.filter((item) => item.id !== id))
            })
          }
        })
        .catch((err) => {
          console.error(err)
          showSwal('Error', 'Failed to delete role.', 'error')
        })
        .finally()
    })
  }

  const fetchAllRoles = () => {
    setInternalLoading(true)
    getAllRoles(axiosPrivate)
      .then((res) => {
        const resData = res.data.data
        setRoles(resData.roles)
      })
      .catch((e) => {
        console.error(e)
      })
      .finally(() => setInternalLoading(false))
  }

  useEffect(() => {
    fetchAllRoles()
  }, [])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {intl.formatMessage({id: subject})}
      </PageTitle>
      <Row className='mb-5'>
        <Col>
          <Card className='card-custom card-border'>
            <Card.Body className='d-flex align-items-center'>
              <div className='d-flex flex-column'>
                <h3 className='text-primary'>{intl.formatMessage({id: subject})}</h3>
                <div className='text-gray-700'>
                  In this module, you can manage <strong>available roles</strong>.
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Header className='card-header-stretch'>
              <Card.Title className='w-100'>
                <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                  <div className='title d-flex align-items-center'>
                    <span className='fa fa-file-alt px-2'></span>
                    <h3 className='text-gray-700'>Manage Available Roles</h3>
                  </div>
                  {ability.can('create', 'MENU.MANAGE_AVAILABLE_ROLES') ? (
                    <Link
                      to={'/manage-roles/add'}
                      className='btn btn-primary d-flex gap-4 align-items-center'
                    >
                      <span className='fa fa-plus'></span>
                      Add
                    </Link>
                  ) : (
                    ''
                  )}
                </div>
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {!internalLoading ? (
                <DataTable<Role>
                  data={roles}
                  columns={columns}
                  enablePagination={true}
                  enableSorting={true}
                  enableGlobalFilter={true}
                  globalFilterPlaceholder='Search Admin Username'
                  defaultPageSize={10}
                  pageSizes={[5, 10, 20, 50, 100]}
                  className='my-custom-table-wrapper'
                />
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{height: '50vh'}}
                >
                  <Spinner animation='border' role='status' variant='primary' className='me-3' />
                  <div className='loading-text'>
                    Loading
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ManageAvailableRolesPage
